import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, EyeIcon, AlertTriangleIcon, StoreIcon, ExternalLinkIcon, PauseIcon, PlayIcon, ShieldIcon, DownloadIcon, UserIcon, CreditCardIcon, UserPlusIcon, PowerIcon, ArrowRightLeftIcon, ClockIcon, CheckCircleIcon, XCircleIcon, Loader2Icon, FileTextIcon, BellIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { DeleteAccountModal } from '../../components/DeleteAccountModal';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
// Account status types
type AccountStatus = 'active' | 'deactivated' | 'pending_deletion' | 'restricted';
// Data export format types
type ExportFormat = 'json' | 'csv' | 'pdf';
// Account role types
type AccountRole = 'buyer' | 'seller' | 'affiliate';
// Subscription status types
type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'none';
export const AccountActionsSection = () => {
  const navigate = useNavigate();
  const {
    user,
    hasRole,
    addRole,
    switchRole
  } = useAuth();
  // State variables
  const [storeOnline, setStoreOnline] = useState(true);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('active');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [currentRole, setCurrentRole] = useState<AccountRole>('buyer');
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('none');
  const [isMergingAccount, setIsMergingAccount] = useState(false);
  const [mergeEmail, setMergeEmail] = useState('');
  const [mergeSuccess, setMergeSuccess] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  // Fetch user account status and data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;
      try {
        const userDocRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Set account status
          if (userData.accountStatus) {
            setAccountStatus(userData.accountStatus as AccountStatus);
          }
          // Set store status
          if (userData.storeOnline !== undefined) {
            setStoreOnline(userData.storeOnline);
          }
          // Set current role
          if (userData.activeRole) {
            setCurrentRole(userData.activeRole as AccountRole);
          } else if (user.activeRole) {
            setCurrentRole(user.activeRole as AccountRole);
          }
          // Set subscription status
          if (userData.subscriptionStatus) {
            setSubscriptionStatus(userData.subscriptionStatus as SubscriptionStatus);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [user]);
  // Handle store status toggle
  const toggleStoreStatus = async () => {
    if (!user?.id) return;
    try {
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, {
        storeOnline: !storeOnline
      });
      setStoreOnline(!storeOnline);
      // Show status message
      setStatusMessage(!storeOnline ? 'Your store is now online and visible to buyers' : 'Your store is now offline and hidden from buyers');
      setShowStatusMessage(true);
      // Hide status message after 3 seconds
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating store status:', error);
    }
  };
  // Handle account deactivation
  const handleDeactivateAccount = async () => {
    if (!user?.id) return;
    // Confirm deactivation
    const confirmed = window.confirm('Are you sure you want to deactivate your account? You can reactivate it at any time by logging in again.');
    if (!confirmed) return;
    setIsDeactivating(true);
    try {
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, {
        accountStatus: 'deactivated',
        deactivatedAt: serverTimestamp()
      });
      // Update local state
      setAccountStatus('deactivated');
      // Show status message
      setStatusMessage('Your account has been deactivated. You will be logged out shortly.');
      setShowStatusMessage(true);
      // Log the user out after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error deactivating account:', error);
      setStatusMessage('Failed to deactivate account. Please try again.');
      setShowStatusMessage(true);
    } finally {
      setIsDeactivating(false);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    }
  };
  // Handle account reactivation
  const handleReactivateAccount = async () => {
    if (!user?.id) return;
    setIsReactivating(true);
    try {
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, {
        accountStatus: 'active',
        reactivatedAt: serverTimestamp()
      });
      // Update local state
      setAccountStatus('active');
      // Show status message
      setStatusMessage('Your account has been reactivated.');
      setShowStatusMessage(true);
    } catch (error) {
      console.error('Error reactivating account:', error);
      setStatusMessage('Failed to reactivate account. Please try again.');
      setShowStatusMessage(true);
    } finally {
      setIsReactivating(false);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    }
  };
  // Handle data export
  const handleExportData = async () => {
    if (!user?.id) return;
    setIsExporting(true);
    setExportSuccess(false);
    try {
      // Log export request
      await addDoc(collection(db, 'dataExportRequests'), {
        userId: user.id,
        requestedAt: serverTimestamp(),
        format: exportFormat,
        status: 'pending'
      });
      // Simulate export process (in a real app, this would be handled by a backend process)
      setTimeout(() => {
        setIsExporting(false);
        setExportSuccess(true);
        // Reset success state after 3 seconds
        setTimeout(() => {
          setExportSuccess(false);
        }, 3000);
      }, 2000);
    } catch (error) {
      console.error('Error requesting data export:', error);
      setIsExporting(false);
    }
  };
  // Handle role change
  const handleRoleChange = async (newRole: AccountRole) => {
    if (!user?.id || currentRole === newRole) return;
    setIsChangingRole(true);
    try {
      // Check if user has the role
      if (!hasRole(newRole)) {
        // Add the role if they don't have it
        await addRole(newRole);
      }
      // Switch to the role
      await switchRole(newRole);
      // Update user document
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, {
        activeRole: newRole
      });
      // Update local state
      setCurrentRole(newRole);
      // Show status message
      setStatusMessage(`Your account role has been changed to ${newRole}.`);
      setShowStatusMessage(true);
    } catch (error) {
      console.error('Error changing role:', error);
      setStatusMessage('Failed to change account role. Please try again.');
      setShowStatusMessage(true);
    } finally {
      setIsChangingRole(false);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    }
  };
  // Handle account merge request
  const handleAccountMergeRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !mergeEmail.trim()) return;
    setIsMergingAccount(true);
    setMergeSuccess(false);
    setMergeError('');
    try {
      // Create merge request
      await addDoc(collection(db, 'accountMergeRequests'), {
        requestorId: user.id,
        requestorEmail: user.email,
        targetEmail: mergeEmail.trim(),
        status: 'pending',
        requestedAt: serverTimestamp()
      });
      // Show success message
      setMergeSuccess(true);
      setMergeEmail('');
      // Reset success state after 3 seconds
      setTimeout(() => {
        setMergeSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error requesting account merge:', error);
      setMergeError('Failed to submit account merge request. Please try again.');
    } finally {
      setIsMergingAccount(false);
    }
  };
  // Handle view store click
  const handleViewStore = () => {
    // Open seller profile in new tab
    window.open(`/seller/${user?.id || 'profile'}`, '_blank');
  };
  // Handle manage subscription click
  const handleManageSubscription = () => {
    navigate('/account-settings/payments');
  };
  // Render account status badge
  const renderStatusBadge = () => {
    switch (accountStatus) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" data-id="element-3279">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3280" />
            Active
          </span>;
      case 'deactivated':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300" data-id="element-3281">
            <PauseIcon className="w-3 h-3 mr-1" data-id="element-3282" />
            Deactivated
          </span>;
      case 'pending_deletion':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-id="element-3283">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3284" />
            Pending Deletion
          </span>;
      case 'restricted':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" data-id="element-3285">
            <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-3286" />
            Restricted
          </span>;
      default:
        return null;
    }
  };
  // Render subscription status badge
  const renderSubscriptionBadge = () => {
    switch (subscriptionStatus) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" data-id="element-3287">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3288" />
            Active
          </span>;
      case 'canceled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300" data-id="element-3289">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-3290" />
            Canceled
          </span>;
      case 'past_due':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-id="element-3291">
            <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-3292" />
            Past Due
          </span>;
      case 'trialing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-3293">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3294" />
            Trial
          </span>;
      case 'none':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300" data-id="element-3295">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-3296" />
            No Subscription
          </span>;
      default:
        return null;
    }
  };
  return <div data-id="element-3297">
      <div className="flex justify-between items-center mb-6" data-id="element-3298">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3299">
          Account Actions
        </h2>
        <div className="flex items-center" data-id="element-3300">
          <span className="mr-2 text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3301">
            Status:
          </span>
          {renderStatusBadge()}
        </div>
      </div>

      {/* Status Message */}
      {showStatusMessage && <div className="mb-6 p-3 bg-[#2DB489]/10 text-[#2DB489] dark:text-[#4ED1A0] rounded-md flex items-start" data-id="element-3302">
          <BellIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3303" />
          <p data-id="element-3304">{statusMessage}</p>
        </div>}

      <div className="space-y-8" data-id="element-3305">
        {/* Account Status Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3306">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3307">
            <div className="flex items-center" data-id="element-3308">
              <UserIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3309" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3310">
                Account Status
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3311">
            <div className="flex items-center justify-between mb-4" data-id="element-3312">
              <div data-id="element-3313">
                <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3314">
                  {accountStatus === 'active' ? 'Your account is active' : 'Your account is deactivated'}
                </h4>
                <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3315">
                  {accountStatus === 'active' ? 'Your account is fully functional and accessible' : 'Your account is deactivated and has limited functionality'}
                </p>
              </div>
              {accountStatus === 'active' ? <button onClick={handleDeactivateAccount} disabled={isDeactivating} className="flex items-center px-4 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md text-sm font-medium text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3316">
                  {isDeactivating ? <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3317" />
                      Deactivating...
                    </> : <>
                      <PowerIcon className="w-4 h-4 mr-2" data-id="element-3318" />
                      Deactivate Account
                    </>}
                </button> : <button onClick={handleReactivateAccount} disabled={isReactivating} className="flex items-center px-4 py-2 bg-[#2DB489] hover:bg-[#2DB489]/90 dark:bg-[#4ED1A0] dark:hover:bg-[#4ED1A0]/90 text-white rounded-md text-sm font-medium transition-colors" data-id="element-3319">
                  {isReactivating ? <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3320" />
                      Reactivating...
                    </> : <>
                      <PlayIcon className="w-4 h-4 mr-2" data-id="element-3321" />
                      Reactivate Account
                    </>}
                </button>}
            </div>
            {accountStatus === 'deactivated' && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 rounded-md p-4 mt-2" data-id="element-3322">
                <div className="flex" data-id="element-3323">
                  <AlertTriangleIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0" data-id="element-3324" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-3325">
                    While your account is deactivated, your store will be hidden
                    and you won't receive notifications. You can reactivate your
                    account at any time.
                  </p>
                </div>
              </div>}
          </div>
        </section>

        {/* Store Visibility Section */}
        {accountStatus === 'active' && hasRole('seller') && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3326">
            <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3327">
              <div className="flex items-center" data-id="element-3328">
                <StoreIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3329" />
                <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3330">
                  Store Visibility
                </h3>
              </div>
            </div>
            <div className="p-5" data-id="element-3331">
              <div className="flex items-center justify-between mb-4" data-id="element-3332">
                <div data-id="element-3333">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3334">
                    {storeOnline ? 'Your store is online' : 'Your store is offline'}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3335">
                    {storeOnline ? 'Your store is visible to all buyers' : 'Your store is currently offline and hidden from buyers'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3336">
                  <input type="checkbox" className="sr-only peer" checked={storeOnline} onChange={toggleStoreStatus} data-id="element-3337" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3338"></div>
                </label>
              </div>
              {!storeOnline && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 rounded-md p-4 mt-4" data-id="element-3339">
                  <div className="flex" data-id="element-3340">
                    <PauseIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0" data-id="element-3341" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-3342">
                      While your store is offline, buyers cannot see your
                      products or make purchases. You can still access your
                      dashboard and manage your products.
                    </p>
                  </div>
                </div>}
              <div className="mt-6" data-id="element-3343">
                <button onClick={handleViewStore} className="flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3344">
                  <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-3345" />
                  View My Store
                </button>
              </div>
            </div>
          </section>}

        {/* Data Export Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3346">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3347">
            <div className="flex items-center" data-id="element-3348">
              <DownloadIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3349" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3350">
                Data Export
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3351">
            <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-3352">
              Export all your account data, including personal information,
              orders, and preferences. This process may take a few minutes.
            </p>
            {exportSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3353">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3354" />
                <p data-id="element-3355">
                  Your data export request has been submitted. You will receive
                  an email with a download link when it's ready.
                </p>
              </div>}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-3356">
              <div className="flex-1" data-id="element-3357">
                <label htmlFor="export-format" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3358">
                  Export Format
                </label>
                <select id="export-format" value={exportFormat} onChange={e => setExportFormat(e.target.value as ExportFormat)} className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" data-id="element-3359">
                  <option value="json" data-id="element-3360">JSON</option>
                  <option value="csv" data-id="element-3361">CSV</option>
                  <option value="pdf" data-id="element-3362">PDF</option>
                </select>
              </div>
              <div className="flex-1 flex items-end" data-id="element-3363">
                <button onClick={handleExportData} disabled={isExporting} className="w-full px-4 py-2 bg-[#2DB489] hover:bg-[#2DB489]/90 dark:bg-[#4ED1A0] dark:hover:bg-[#4ED1A0]/90 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center" data-id="element-3364">
                  {isExporting ? <>
                      <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3365" />
                      Processing...
                    </> : <>
                      <DownloadIcon className="w-4 h-4 mr-2" data-id="element-3366" />
                      Export My Data
                    </>}
                </button>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-[#7A8F8A] flex items-center" data-id="element-3367">
              <FileTextIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-3368" />
              <span data-id="element-3369">
                Your data will be provided in accordance with GDPR and our
                Privacy Policy
              </span>
            </div>
          </div>
        </section>

        {/* Account Role Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3370">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3371">
            <div className="flex items-center" data-id="element-3372">
              <ArrowRightLeftIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3373" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3374">
                Account Roles
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3375">
            <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-3376">
              Switch between different roles to access different features of the
              platform.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3377">
              <button onClick={() => handleRoleChange('buyer')} disabled={isChangingRole || currentRole === 'buyer'} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-colors ${currentRole === 'buyer' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3378">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${currentRole === 'buyer' ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0]' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-3379">
                  <UserIcon className="w-5 h-5" data-id="element-3380" />
                </div>
                <span className={`font-medium ${currentRole === 'buyer' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3381">
                  Buyer
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3382">
                  Browse and purchase products
                </span>
              </button>
              <button onClick={() => handleRoleChange('seller')} disabled={isChangingRole || currentRole === 'seller'} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-colors ${currentRole === 'seller' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3383">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${currentRole === 'seller' ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0]' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-3384">
                  <StoreIcon className="w-5 h-5" data-id="element-3385" />
                </div>
                <span className={`font-medium ${currentRole === 'seller' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3386">
                  Seller
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3387">
                  Sell products and manage store
                </span>
              </button>
              <button onClick={() => handleRoleChange('affiliate')} disabled={isChangingRole || currentRole === 'affiliate'} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-colors ${currentRole === 'affiliate' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3388">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${currentRole === 'affiliate' ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0]' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-3389">
                  <UserPlusIcon className="w-5 h-5" data-id="element-3390" />
                </div>
                <span className={`font-medium ${currentRole === 'affiliate' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3391">
                  Affiliate
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3392">
                  Promote products and earn commission
                </span>
              </button>
            </div>
            {isChangingRole && <div className="flex justify-center items-center py-2" data-id="element-3393">
                <Loader2Icon className="w-5 h-5 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3394" />
                <span className="ml-2 text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3395">
                  Changing role...
                </span>
              </div>}
          </div>
        </section>

        {/* Subscription Management Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3396">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3397">
            <div className="flex items-center" data-id="element-3398">
              <CreditCardIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3399" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3400">
                Subscription Management
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3401">
            <div className="flex items-center justify-between mb-4" data-id="element-3402">
              <div data-id="element-3403">
                <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3404">
                  Current Subscription
                </h4>
                <div className="flex items-center mt-1" data-id="element-3405">
                  {renderSubscriptionBadge()}
                  <span className="ml-2 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3406">
                    {subscriptionStatus === 'none' ? 'No active subscription' : subscriptionStatus === 'active' ? 'Your subscription is active' : `Subscription status: ${subscriptionStatus}`}
                  </span>
                </div>
              </div>
              <button onClick={handleManageSubscription} className="flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3407">
                <CreditCardIcon className="w-4 h-4 mr-2" data-id="element-3408" />
                Manage Subscription
              </button>
            </div>
            {subscriptionStatus === 'past_due' && <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-md p-4 mt-2" data-id="element-3409">
                <div className="flex" data-id="element-3410">
                  <AlertTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" data-id="element-3411" />
                  <p className="text-sm text-red-700 dark:text-red-300" data-id="element-3412">
                    Your payment is past due. Please update your payment method
                    to avoid service interruption.
                  </p>
                </div>
              </div>}
          </div>
        </section>

        {/* Account Merging Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3413">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3414">
            <div className="flex items-center" data-id="element-3415">
              <UserPlusIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3416" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3417">
                Account Merging
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3418">
            <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-3419">
              Request to merge another account with this one. Both accounts must
              be verified and you must have access to both email addresses.
            </p>
            {mergeSuccess && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-md flex items-start" data-id="element-3420">
                <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3421" />
                <p data-id="element-3422">
                  Your account merge request has been submitted. You will
                  receive an email with further instructions.
                </p>
              </div>}
            {mergeError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md flex items-start" data-id="element-3423">
                <AlertTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3424" />
                <p data-id="element-3425">{mergeError}</p>
              </div>}
            <form onSubmit={handleAccountMergeRequest} data-id="element-3426">
              <div className="space-y-4" data-id="element-3427">
                <div data-id="element-3428">
                  <label htmlFor="merge-email" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3429">
                    Email of Account to Merge
                  </label>
                  <input type="email" id="merge-email" value={mergeEmail} onChange={e => setMergeEmail(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9]" placeholder="email@example.com" data-id="element-3430" />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 rounded-md p-4" data-id="element-3431">
                  <div className="flex" data-id="element-3432">
                    <AlertTriangleIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0" data-id="element-3433" />
                    <p className="text-sm text-blue-700 dark:text-blue-300" data-id="element-3434">
                      Merging accounts is permanent and cannot be undone. You
                      will need to verify ownership of both accounts.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-3435">
                  <button type="submit" disabled={isMergingAccount || !mergeEmail.trim()} className="px-4 py-2 bg-[#2DB489] hover:bg-[#2DB489]/90 dark:bg-[#4ED1A0] dark:hover:bg-[#4ED1A0]/90 text-white rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-70" data-id="element-3436">
                    {isMergingAccount ? <>
                        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-3437" />
                        Submitting...
                      </> : <>
                        <UserPlusIcon className="w-4 h-4 mr-2" data-id="element-3438" />
                        Request Account Merge
                      </>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {/* Account Deletion Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3439">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3440">
            <div className="flex items-center" data-id="element-3441">
              <ShieldIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3442" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3443">
                Account Deletion
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3444">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-md p-4 mb-6" data-id="element-3445">
              <div className="flex" data-id="element-3446">
                <AlertTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" data-id="element-3447" />
                <div data-id="element-3448">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-3449">
                    Warning
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-3450">
                    This will permanently delete your account, products, and
                    data. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <button onClick={() => setDeleteAccountModalOpen(true)} className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white rounded-lg transition-colors" data-id="element-3451">
              <TrashIcon className="w-4 h-4 mr-2" data-id="element-3452" />
              Delete My Account
            </button>
          </div>
        </section>
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal isOpen={deleteAccountModalOpen} onClose={() => setDeleteAccountModalOpen(false)} data-id="element-3453" />
    </div>;
};